import React, { useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import clsx from 'clsx';
import parse from 'html-react-parser';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LocalPhoneOutlined } from '@mui/icons-material';
import useGetPartnerProfile from '../../hooks/useGetPartnerProfile';
import { useMarketingCreatives } from './useMarketingCreatives';
import { ArrowBack } from '../../assets/mui';

interface Tag {
  title: string;
  color: string;
}

interface CreativeCard {
  title: string;
  description: string;
  datePosted: string;
  tags: Tag[];
  views: string;
  image?: string;
  sessionDetails?: any;
  contact?: any;
  actions?: string[];
}

const MarketingCreativeSection: React.FC = () => {
  const { sectionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: marketingCreative } = useMarketingCreatives();
  const { data } = useGetPartnerProfile();
  const [selectedCard, setSelectedCard] = useState<CreativeCard | null>(null);

  const captureRef = useRef();

  // Get section data from location state or fallback to API data
  const sectionData =
    location.state?.sectionData || marketingCreative?.[parseInt(sectionId || '0')];
  const sectionTitle =
    location.state?.sectionTitle || sectionData?.sectionTitle || 'Marketing Creatives';

  const getBadgeColor = (color: string) => {
    return color === 'red' ? 'bg-red-600' : 'bg-green-600';
  };

  const handleDownload = async () => {
    if (!selectedCard) return;

    const canvas = await html2canvas(captureRef.current, {
      useCORS: true,
      scale: 2,
    });
    const link = document.createElement('a');
    link.download = `${selectedCard.title || 'card'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleBack = () => {
    navigate('/marketing-creatives');
  };

  if (!sectionData) {
    return (
      <div className="flex flex-col max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-4 mb-6 mt-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowBackIcon fontSize="small" />
            Back
          </button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-600">Section not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col max-w-7xl mx-auto px-4">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6 mt-6">
        <button
          onClick={handleBack}
          className="bg-green-700/10 flex items-center justify-center p-1.5 h-9 w-9 rounded-full text-green-700"
        >
          <ArrowBack fontSize="small" />
        </button>
        <h1 className="font-medium text-xl text-[#1C1B1F]">{sectionTitle}</h1>
      </div>

      {/* Grid of all items */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
        {sectionData.list?.map((card: CreativeCard, i: number) => (
          <div
            key={i}
            onClick={() => setSelectedCard(card)}
            className="w-[254px] h-[335px] flex flex-col bg-white border border-[#E5E5E5] rounded-[12px] cursor-pointer shadow-sm hover:shadow-md transition"
          >
            {/* Image Block */}
            <div
              className="relative w-full h-[254px] bg-black bg-opacity-[0.02] rounded-t-[11px] overflow-hidden"
              style={{
                backgroundImage: card.image ? `url(${card.image})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              {/* LIVE/NEW Tags + Views */}
              <div className="absolute top-[14px] right-[14px] flex gap-[6px]">
                {card.tags?.map((tag, tIdx) => (
                  <div
                    key={tIdx}
                    className={`flex items-center justify-center px-2 py-1 rounded-[4px] text-white text-[10px] font-semibold leading-[15px] tracking-[0.08em] uppercase ${
                      tag.color === 'red' ? 'bg-[#B91C1C]' : 'bg-[#22C55E]'
                    }`}
                  >
                    {tag.title}
                  </div>
                ))}
                <div className="flex items-center justify-center px-2 py-1 rounded-[4px] bg-[#E5E5E5] text-[#171717] text-[10px] font-semibold leading-[15px] tracking-[0.08em] uppercase">
                  {card.views} Views
                </div>
              </div>
            </div>

            {/* Content Block */}
            <div className="flex flex-col justify-between p-4 gap-4 border-t border-[#E7E5E4] h-[81px]">
              <div className="flex flex-col gap-[4px]">
                <h3 className="text-[16px] font-medium text-[#171717] leading-[24px] truncate">
                  {card.title}
                </h3>
                <p className="text-[14px] font-normal text-[#737373] leading-[21px] truncate">
                  {card.datePosted}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-[#fafafa] rounded-lg max-w-4xl w-full mx-4 overflow-y-auto max-h-[90vh] relative">
            {/* Close Button */}
            <CloseIcon
              className="absolute top-5 right-5 font-bold text-neutral-500 hover:text-black cursor-pointer"
              onClick={() => setSelectedCard(null)}
              fontSize={'small'}
            />

            {/* Content Section */}
            <div className="flex flex-col sm:flex-row">
              {/* Left: Image */}
              <div
                ref={captureRef}
                className={`bg-white rounded-lg gap-2 flex flex-col p-[14px] w-[300px] m-9`}
                style={{
                  boxShadow: `
                    0px 1.1px 2.21px 0px #00000005,
                    0px 5.52px 5.52px 0px #00000003,
                    0px 11.04px 6.62px 0px #00000003,
                    0px 19.87px 7.73px 0px #00000000,
                    0px 30.91px 8.83px 0px #00000000
                  `,
                }}
              >
                <img
                  src={selectedCard.image}
                  alt={selectedCard.title}
                  className="w-fit h-fit object-cover rounded"
                />
                <div className="flex justify-between items-start w-full h-full">
                  {/* Left: Name and Phone */}
                  <div className="flex flex-col">
                    <h3 className="text-sm font-semibold text-neutral-950 mb-1">Arpit</h3>
                    <div className="flex items-center text-xs text-neutral-500 ">
                      <LocalPhoneOutlined style={{ fontSize: '12px', marginRight: '4px' }} />
                      <div className="text-xs">95014 78900</div>
                    </div>
                  </div>

                  {/* Right: Powered by logo */}
                  <div className="flex flex-col items-end gap-1">
                    <h4
                      className="text-neutral-400 uppercase"
                      style={{ fontSize: '6px', letterSpacing: '-0.01em' }}
                    >
                      Powered by
                    </h4>
                    <div className="p-1 bg-black rounded-md flex items-center justify-center">
                      <img
                        alt="tap logo"
                        src="/logo-dark.png"
                        height={14}
                        width={14}
                        className="block"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Info */}
              <div className="w-full sm:w-1/2 py-auto border-l border-neutral-200 p-6">
                {/* Tags */}
                <div className="flex items-start gap-2 mb-2 flex-wrap mt-10">
                  {selectedCard.tags?.map((tag, idx) => (
                    <span
                      key={idx}
                      className={clsx(
                        'px-2 py-1 text-xs text-white rounded-[4px]',
                        getBadgeColor(tag.color),
                      )}
                    >
                      {tag.title}
                    </span>
                  ))}
                  <span className="text-xs bg-gray-200 px-2 py-1 rounded-[4px] text-gray-700">
                    {selectedCard.views} VIEWS
                  </span>
                </div>

                {/* Title */}
                <h2 className="text-lg font-semibold mb-2">{selectedCard.title}</h2>

                {/* Description */}
                <div className="text-sm text-gray-700 space-y-2">
                  {parse(selectedCard.description)}
                </div>

                {/* Session Details */}
                <div className="mt-4 text-sm text-gray-700 space-y-1">
                  <p>
                    <strong>Referral Link:</strong>{' '}
                    <a
                      href={`https://partner.tapinvest.in/login?refCode=${data?.data?.referralCodes[0]?.referralCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline break-all"
                    >
                      https://partner.tapinvest.in/login?refCode=$
                      {data?.data?.referralCodes[0]?.referralCode}
                    </a>
                  </p>
                </div>

                {/* Contact Section */}
                {selectedCard.contact && (
                  <div className="mt-4 text-sm text-gray-700">
                    <p>
                      <strong>{selectedCard.contact.name}</strong> 📞 {selectedCard.contact.phone}
                    </p>
                    <p>{selectedCard.contact.company}</p>
                    <p className="text-xs text-gray-500">
                      Powered by {selectedCard.contact.poweredBy}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-end gap-3 border-t border-neutral-200 p-5">
              <button
                className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px]  h-[38px] bg-[rgba(23,23,23,0.08)] rounded-[8px] box-border"
                style={{ lineHeight: '18px', color: '#0A0A0A' }}
              >
                <WhatsAppIcon sx={{ width: 14, height: 14.11 }} />
                <span className=" font-semibold text-center text-xs">WhatsApp</span>
              </button>
              <button
                className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px]  h-[38px] bg-[rgba(23,23,23,0.08)] rounded-[8px] box-border"
                style={{ lineHeight: '18px', color: '#0A0A0A' }}
              >
                <DraftsOutlinedIcon sx={{ width: 14, height: 14.11 }} />
                <span className=" font-semibold text-center text-xs">Email</span>
              </button>
              <button
                className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px]  h-[38px] bg-[rgba(23,23,23,0.08)] rounded-[8px] box-border"
                style={{ lineHeight: '18px', color: '#0A0A0A' }}
              >
                <ContentPasteIcon sx={{ width: 14, height: 14.11 }} />
                <span className=" font-semibold text-center text-xs text-neutral-950">Copy</span>
              </button>
              <button
                className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px]  h-[38px] bg-black  rounded-[8px] box-border"
                style={{ lineHeight: '18px', color: 'white' }}
                onClick={handleDownload}
              >
                <FileDownloadOutlinedIcon sx={{ width: 16, height: 16 }} />
                <span className=" font-semibold text-center text-xs">Download</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MarketingCreativeSection.displayName = 'MarketingCreativeSection';
export default MarketingCreativeSection;
